/**
 * THE PERIOD FILTER — the one control every period-taking screen wears (v19: the country
 * dashboard sections, the group view, the four reports).
 *
 * THE CHIPS SAY WHAT WAS ASKED. The `resolved-range` sentence underneath says what the
 * SERVER USED — resolved in the country's calendar (or HQ's, for the group view) and
 * echoed back as `window`. The two can differ across a timezone, and on a reporting screen
 * the second is the one that matters. Nothing here computes a date from the browser clock
 * (the RenewalsPage rule, applied verbatim).
 *
 * The state lives in the URL (`?period=&from=&to=`): a colleague opening your link sees
 * your window, and Back works. Filter changes REPLACE history and clear paging; `custom`
 * is the two date inputs, not a chip. `allowAll` adds an "All time" chip that REMOVES the
 * period — the Parties report's lifetime mode (`window: null` on the wire).
 */

import { useSearchParams } from 'react-router-dom';

import { cn } from '../../lib/cn';
import { PERIOD_CHIPS, formatDateOnlyRange, windowBounds } from './periods';
import type { PeriodWindowOnWire } from './periods';

export function PeriodFilter({
  id,
  allowAll = false,
  resolved,
  defaultKey = 'this_month',
}: {
  /** Prefix for the two date inputs' ids — two filters on one page must not collide. */
  id: string;
  /** Adds an "All time" chip that deletes the period (lifetime mode). */
  allowAll?: boolean;
  /** The window the server applied, when the payload has arrived. `null` = lifetime. */
  resolved: PeriodWindowOnWire | null | undefined;
  defaultKey?: string;
}): React.JSX.Element {
  const [params, setParams] = useSearchParams();
  const rawPeriod = params.get('period');
  const hasPeriod = rawPeriod !== null && rawPeriod !== '';
  const active = hasPeriod ? rawPeriod : allowAll ? 'all' : defaultKey;
  const from = params.get('from') ?? '';
  const to = params.get('to') ?? '';

  /** Filters REPLACE history and reset paging — page 3 of last month is not page 3 of FYTD. */
  const patch = (apply: (next: URLSearchParams) => void): void => {
    const next = new URLSearchParams(params);
    apply(next);
    next.delete('page');
    setParams(next, { replace: true });
  };

  const setPeriod = (value: string | null): void => {
    patch((next) => {
      if (value === null) next.delete('period');
      else next.set('period', value);
      // A quick period has no custom bounds; leaving them would make the URL describe a
      // window the server did not apply.
      next.delete('from');
      next.delete('to');
    });
  };

  const setCustom = (which: 'from' | 'to', value: string): void => {
    patch((next) => {
      next.set('period', 'custom');
      if (value === '') next.delete(which);
      else next.set(which, value);
    });
  };

  const bounds = resolved === null || resolved === undefined ? null : windowBounds(resolved);

  return (
    <div className="flex flex-col gap-2" data-testid={`${id}-period-filter`}>
      <div className="flex flex-wrap items-center gap-2">
        <div role="group" aria-label="Filter by period" className="flex flex-wrap gap-2">
          {allowAll ? (
            <PeriodChip label="All time" active={active === 'all'} onClick={() => setPeriod(null)} />
          ) : null}
          {PERIOD_CHIPS.map((chip) => (
            <PeriodChip
              key={chip.value}
              label={chip.label}
              active={active === chip.value}
              onClick={() => setPeriod(chip.value)}
            />
          ))}
        </div>
        <div className="flex items-center gap-2">
          <label className="text-body13 text-fg-secondary" htmlFor={`${id}-from`}>
            From
          </label>
          <input
            id={`${id}-from`}
            type="date"
            value={from}
            onChange={(event) => setCustom('from', event.target.value)}
            className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
          />
          <label className="text-body13 text-fg-secondary" htmlFor={`${id}-to`}>
            To
          </label>
          <input
            id={`${id}-to`}
            type="date"
            value={to}
            onChange={(event) => setCustom('to', event.target.value)}
            className="h-8 rounded-sm border border-line bg-surface px-2 text-body13 text-fg"
          />
        </div>
      </div>

      {/* THE WINDOW THE SERVER APPLIED, IN WORDS — an answer, never an echo of the chip. */}
      {resolved === undefined ? null : resolved === null ? (
        <p className="text-body13 text-fg-secondary" data-testid="resolved-range">
          All time — every record, no window applied.
        </p>
      ) : (
        <p className="text-body13 text-fg-secondary" data-testid="resolved-range">
          {formatDateOnlyRange(bounds?.from, bounds?.to)}
          {resolved.tz === undefined ? '' : ` · ${resolved.tz}`}
        </p>
      )}
    </div>
  );
}

function PeriodChip({
  label,
  active,
  onClick,
}: {
  label: string;
  active: boolean;
  onClick: () => void;
}): React.JSX.Element {
  return (
    <button
      type="button"
      aria-pressed={active}
      onClick={onClick}
      className={cn(
        'h-8 rounded-sm border border-line bg-surface px-2.5 text-body13 text-fg-secondary',
        active && 'border-line-strong font-medium text-fg',
      )}
    >
      {label}
    </button>
  );
}
